/**
 * Import dialog for MSSQL database .bacpac import.
 * Shows file selection, target database name input, and import progress.
 * Warns if target database already exists.
 */

import { useCallback, useEffect, useState } from 'react';
import { Database, Minus, AlertTriangle, Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { ProgressIndicator } from './ProgressIndicator';
import { useBackupImport } from '../hooks/useBackupImport';
import { useOperationStatus } from '../hooks/useTaskProgress';
import { useBackupRestoreStore } from '../store/backupRestoreStore';
import { checkDatabaseExists } from '../services/backupRestoreApi';
import { toast } from '../../../components/ui/toaster';

type ImportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
};

type ImportStage = 'input' | 'importing';

export function ImportDialog({
  open,
  onOpenChange,
  connectionId,
}: ImportDialogProps) {
  const [stage, setStage] = useState<ImportStage>('input');
  const [filePath, setFilePath] = useState('');
  const [targetDatabaseName, setTargetDatabaseName] = useState('');
  const [databaseExists, setDatabaseExists] = useState(false);
  const [isCheckingDb, setIsCheckingDb] = useState(false);
  const [operationId, setOperationId] = useState<string | null>(null);

  const operation = useOperationStatus(operationId);
  const { removeOperation, minimizeOperation, isMinimized } = useBackupRestoreStore();

  // Check if this operation is minimized
  const operationIsMinimized = operationId ? isMinimized(operationId) : false;

  const { importDatabase, isImporting, reset } = useBackupImport({
    onSuccess: (result, opId) => {
      setOperationId(opId);

      if (result.cancelled) {
        // User cancelled, go back to input
        setStage('input');
        return;
      }

      if (!result.success) {
        toast.error({
          title: 'Import Failed',
          description: result.error || 'Unknown error occurred',
        });
      }
    },
    onError: (error) => {
      toast.error({
        title: 'Import Failed',
        description: error.message,
      });
    },
  });

  // Show success toast when operation completes
  useEffect(() => {
    if (operation?.status === 'succeeded') {
      toast.success({
        title: 'Import Complete',
        description: `Database "${targetDatabaseName}" imported successfully`,
      });
    }
  }, [operation?.status, targetDatabaseName]);

  // Check if database exists when target name changes
  useEffect(() => {
    if (!targetDatabaseName.trim() || !connectionId) {
      setDatabaseExists(false);
      return;
    }

    const checkDb = async () => {
      setIsCheckingDb(true);
      try {
        const result = await checkDatabaseExists(connectionId, targetDatabaseName.trim());
        setDatabaseExists(result.exists);
      } catch {
        setDatabaseExists(false);
      } finally {
        setIsCheckingDb(false);
      }
    };

    // Debounce the check
    const timer = setTimeout(checkDb, 300);
    return () => clearTimeout(timer);
  }, [targetDatabaseName, connectionId]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStage('input');
      setFilePath('');
      setTargetDatabaseName('');
      setDatabaseExists(false);
      setOperationId(null);
      reset();
    }
  }, [open, reset]);

  // Clean up on close (only when not minimized)
  const handleClose = () => {
    if (operationId && operation?.status !== 'inProgress') {
      removeOperation(operationId);
    }
    setOperationId(null);
    reset();
    onOpenChange(false);
  };

  // Minimize the dialog (operation continues in background)
  const handleMinimize = () => {
    if (operationId) {
      minimizeOperation(operationId);
      onOpenChange(false);
    }
  };

  // Start the import
  const handleStartImport = useCallback(() => {
    if (!filePath.trim() || !targetDatabaseName.trim()) return;

    setStage('importing');
    importDatabase({
      connectionId,
      filePath: filePath.trim(),
      targetDatabaseName: targetDatabaseName.trim(),
    });
  }, [connectionId, filePath, targetDatabaseName, importDatabase]);

  // Determine if we can close
  const isComplete =
    operation?.status === 'succeeded' ||
    operation?.status === 'failed' ||
    operation?.status === 'cancelled';
  const canClose = stage === 'input' || isComplete || (!isImporting && operation?.status !== 'inProgress');
  const canMinimize = stage === 'importing' && !isComplete && (isImporting || operation?.status === 'inProgress');

  // Don't show dialog if minimized
  const dialogOpen = open && !operationIsMinimized;

  // Determine if import can start
  const canStartImport =
    filePath.trim().length > 0 &&
    targetDatabaseName.trim().length > 0 &&
    !isCheckingDb;

  return (
    <Dialog open={dialogOpen} onOpenChange={(isOpen) => !isOpen && canClose && handleClose()}>
      <DialogContent data-testid="import-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Upload className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <DialogTitle>Import Data-tier Application</DialogTitle>
              <DialogDescription className="mt-1">
                {stage === 'input'
                  ? 'Import a .bacpac file to create a new database'
                  : `Importing database "${targetDatabaseName}"`}
              </DialogDescription>
            </div>
            {canMinimize && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleMinimize}
                    data-testid="import-dialog-minimize"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  Minimize to status bar
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {stage === 'input' ? (
            <>
              {/* File path input */}
              <div className="space-y-2">
                <Label htmlFor="import-file-path">Source File (.bacpac)</Label>
                <Input
                  id="import-file-path"
                  value={filePath}
                  onChange={(e) => setFilePath(e.target.value)}
                  placeholder="Select or enter .bacpac file path"
                  data-testid="import-file-path-input"
                />
                <p className="text-xs text-muted-foreground">
                  Enter the path to the .bacpac file or leave empty to browse
                </p>
              </div>

              {/* Target database name */}
              <div className="space-y-2">
                <Label htmlFor="import-target-db">Target Database Name</Label>
                <Input
                  id="import-target-db"
                  value={targetDatabaseName}
                  onChange={(e) => setTargetDatabaseName(e.target.value)}
                  placeholder="Enter target database name"
                  data-testid="import-target-db-input"
                />

                {/* Warning if database exists */}
                {databaseExists && (
                  <div className="flex items-center gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-warning">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <p className="text-sm" data-testid="import-db-exists-warning">
                      A database with this name already exists. Import will fail if the database exists.
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            // Import progress
            <>
              {operation ? (
                <ProgressIndicator
                  status={operation.status}
                  progress={operation.progress}
                  message={operation.message}
                  error={operation.error}
                  data-testid="import-progress"
                />
              ) : (
                <ProgressIndicator
                  status="created"
                  message="Preparing import..."
                  data-testid="import-progress"
                />
              )}
            </>
          )}
        </div>

        <DialogFooter>
          {stage === 'input' ? (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                data-testid="import-dialog-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleStartImport}
                disabled={!canStartImport}
                data-testid="import-dialog-start"
              >
                <Database className="h-4 w-4 mr-2" />
                Import
              </Button>
            </>
          ) : (
            <Button
              variant={isComplete ? 'default' : 'outline'}
              onClick={handleClose}
              disabled={!canClose}
              data-testid="import-dialog-close"
            >
              {isComplete ? 'Done' : 'Cancel'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
